import { useEffect, useState } from 'react'
import { usePioneerStore, ChartPlanet, ELEMENT_COLORS } from '../store/pioneerStore'
import {
  ScreenWrapper,
  EngravingDivider,
  GoldButton,
  GoldCard,
  ProgressDots,
  EightStar,
} from '../components/shared'

// ─── Planet labels ────────────────────────────────────────────────────────────

const PLANET_META = {
  sun: {
    symbol: '☉',
    label: 'Sun',
    title: 'Your Core Self',
    subtitle: 'Who you are at the centre of your being.',
  },
  moon: {
    symbol: '☽',
    label: 'Moon',
    title: 'Your Inner World',
    subtitle: 'What you feel, need, and keep close.',
  },
  rising: {
    symbol: '↑',
    label: 'Rising',
    title: 'Your Outer Face',
    subtitle: 'How the world sees you before you speak.',
  },
}

// ─── Individual planet card ───────────────────────────────────────────────────

function PlanetCard({
  planet,
  placement,
  delay,
}: {
  planet: 'sun' | 'moon' | 'rising'
  placement: ChartPlanet | null
  delay: number
}) {
  const [visible, setVisible] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const meta = PLANET_META[planet]
  const elementColor = placement?.element ? ELEMENT_COLORS[placement.element] : '#C9A34A'

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay)
    return () => clearTimeout(t)
  }, [delay])

  if (!placement) {
    return (
      <GoldCard
        className="p-6"
        style={{
          opacity: visible ? 0.5 : 0,
          transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <span style={{ fontSize: '1.6rem', color: '#C9A34A66' }}>{meta.symbol}</span>
          <div>
            <p
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: '0.6rem',
                letterSpacing: '0.2em',
                color: '#C9A34A55',
                textTransform: 'uppercase',
              }}
            >
              {meta.label}
            </p>
          </div>
        </div>
        <p
          style={{
            fontFamily: '"Playfair Display", serif',
            fontStyle: 'italic',
            fontSize: '0.95rem',
            color: '#F5E6C844',
          }}
        >
          Rising sign requires birth time — enter it in the previous step to unlock this placement.
        </p>
      </GoldCard>
    )
  }

  return (
    <GoldCard
      glow
      className="p-6 cursor-pointer"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms, box-shadow 0.3s ease`,
      }}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Element color dot + planet symbol */}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: `${elementColor}18`,
              border: `1px solid ${elementColor}44`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: '1.5rem', color: elementColor }}>{meta.symbol}</span>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.58rem',
                  letterSpacing: '0.2em',
                  color: '#C9A34A88',
                  textTransform: 'uppercase',
                }}
              >
                {meta.label}
              </span>
              {/* Element badge */}
              <span
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.52rem',
                  letterSpacing: '0.1em',
                  color: elementColor,
                  background: `${elementColor}18`,
                  border: `1px solid ${elementColor}44`,
                  padding: '1px 6px',
                  borderRadius: 10,
                  textTransform: 'uppercase',
                }}
              >
                {placement.element}
              </span>
            </div>
            <h3
              style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: 'clamp(1.3rem, 3vw, 1.7rem)',
                color: '#F5E6C8',
                lineHeight: 1.1,
                fontWeight: 700,
              }}
            >
              {placement.glyph ?? ''} {placement.sign}
            </h3>
          </div>
        </div>

        {/* Degree + expand */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.7rem',
              color: '#C9A34A77',
            }}
          >
            {placement.degree.toFixed(1)}°
          </span>
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.58rem',
              color: '#C9A34A44',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {placement.modality}
          </span>
          <span style={{ fontSize: '0.7rem', color: '#C9A34A44', marginTop: 4 }}>
            {expanded ? '▲' : '▼'}
          </span>
        </div>
      </div>

      {/* Subtitle */}
      <p
        style={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '0.75rem',
          color: '#F5E6C855',
          marginTop: '0.5rem',
          marginLeft: '56px',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
        }}
      >
        {meta.title}
      </p>

      {/* Expanded description */}
      <div
        style={{
          maxHeight: expanded ? 300 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.4s ease',
        }}
      >
        <EngravingDivider className="mt-4 mb-3" />
        <p
          style={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.9rem',
            color: '#F5E6C8CC',
            lineHeight: 1.75,
          }}
        >
          {placement.description ?? `${placement.sign} · ${placement.degree.toFixed(1)}°`}
        </p>
      </div>

      {/* Tap hint */}
      {!expanded && (
        <p
          style={{
            marginTop: '0.75rem',
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.12em',
            color: '#C9A34A44',
            textTransform: 'uppercase',
          }}
        >
          Tap to reveal your reading
        </p>
      )}
    </GoldCard>
  )
}

// ─── Screen ───────────────────────────────────────────────────────────────────

// Props from GP1 App.tsx
interface Screen3Props {
  onContinue?: () => void
  onBack?: () => void
}

export default function Screen3ChartReading({ onContinue, onBack }: Screen3Props) {
  const { birthData, chartData, goNext, goBack } = usePioneerStore()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60)
    return () => clearTimeout(t)
  }, [])

  if (!chartData) return null

  return (
    <ScreenWrapper>
      <div
        className="flex flex-col items-center min-h-screen px-6 py-12"
        style={{
          maxWidth: 620,
          margin: '0 auto',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.6s ease',
        }}
      >
        {/* Progress */}
        <ProgressDots current={3} />

        {/* Header */}
        <div className="text-center mt-8 mb-2">
          <div className="flex items-center justify-center gap-2 mb-3">
            <EightStar size={8} />
            <span
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: '0.6rem',
                letterSpacing: '0.22em',
                color: '#C9A34A88',
                textTransform: 'uppercase',
              }}
            >
              Your Cosmic Blueprint
            </span>
            <EightStar size={8} />
          </div>
          <h2
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(1.6rem, 5vw, 2.6rem)',
              fontWeight: 700,
              color: '#F5E6C8',
              lineHeight: 1.15,
            }}
          >
            {birthData?.name
              ? `${birthData.name.split(' ')[0]}, this is`
              : 'This is'}
            <br />
            <em style={{ color: '#C9A34A' }}>who you are</em>
          </h2>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.88rem',
              color: '#F5E6C877',
              marginTop: '0.75rem',
              lineHeight: 1.6,
            }}
          >
            Calculated with Swiss Ephemeris precision.
            Tap each card to read your full interpretation.
          </p>
        </div>

        <EngravingDivider className="w-full my-6" />

        {/* Planet cards */}
        <div className="flex flex-col gap-4 w-full">
          <PlanetCard planet="sun" placement={chartData.sun} delay={200} />
          <PlanetCard planet="moon" placement={chartData.moon} delay={400} />
          <PlanetCard planet="rising" placement={chartData.rising} delay={600} />
        </div>

        {/* Summary strip */}
        <div
          className="w-full mt-6 p-4 flex items-center justify-center gap-6"
          style={{
            background: '#C9A34A08',
            border: '1px solid #C9A34A22',
            borderRadius: 2,
          }}
        >
          {[
            { label: 'Sun',    value: `${chartData.sun.glyph ?? ''}  ${chartData.sun.sign}`.trim() },
            { label: 'Moon',   value: `${chartData.moon.glyph ?? ''} ${chartData.moon.sign}`.trim() },
            chartData.rising
              ? { label: 'Rising', value: `${chartData.rising.glyph ?? ''} ${chartData.rising.sign}`.trim() }
              : null,
          ]
            .filter(Boolean)
            .map((item) => (
              <div key={item!.label} className="text-center">
                <p
                  style={{
                    fontFamily: '"DM Mono", monospace',
                    fontSize: '0.55rem',
                    letterSpacing: '0.18em',
                    color: '#C9A34A66',
                    textTransform: 'uppercase',
                    marginBottom: 2,
                  }}
                >
                  {item!.label}
                </p>
                <p
                  style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '0.95rem',
                    color: '#F5E6C8',
                  }}
                >
                  {item!.value}
                </p>
              </div>
            ))}
        </div>

        {/* Pioneer teaser */}
        <div
          className="w-full mt-6 p-5 text-center"
          style={{
            background: 'linear-gradient(135deg, #C9A34A08 0%, #C9A34A14 100%)',
            border: '1px solid #C9A34A44',
            borderRadius: 2,
          }}
        >
          <EightStar size={14} className="mx-auto mb-3" />
          <p
            style={{
              fontFamily: '"Playfair Display", serif',
              fontStyle: 'italic',
              fontSize: '1rem',
              color: '#F5E6C8CC',
              lineHeight: 1.6,
              marginBottom: '0.5rem',
            }}
          >
            Your chart deserves a permanent home — a Name Card that carries
            this identity everywhere you go.
          </p>
          <p
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.62rem',
              letterSpacing: '0.15em',
              color: '#C9A34A',
              textTransform: 'uppercase',
            }}
          >
            Claim your Pioneer seat → secure it forever
          </p>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between w-full mt-8 gap-4">
          <GoldButton variant="ghost" size="md" onClick={onBack ?? goBack}>
            ← Back
          </GoldButton>
          <GoldButton size="md" onClick={onContinue ?? goNext} style={{ flex: 1 }}>
            Claim My Pioneer Seat →
          </GoldButton>
        </div>
      </div>
    </ScreenWrapper>
  )
}
